/**
 * L'onglet « Travaux et corrections » de la fiche d'un élève — chantier
 * « manuel vivant », Φ-MV-5 (MV-TUT-07, MV-DEF-26, F-11).
 *
 * Une lecture : `GET /v1/tuteur/manuel/eleves/:eleveId/travaux`. Les essais
 * d'exercices, les défis et les devoirs photographiés, RÉUNIS, du plus récent
 * au plus ancien — les mêmes copies que l'historique de l'élève. Le verdict de
 * chaque travail, ses erreurs nommées et la correction ligne par ligne sont
 * écrits par le serveur.
 *
 * ─── La charge (F-11) ───────────────────────────────────────────────────────
 *
 * La liste reste légère : une ligne par travail, repliée. On DÉPLIE un travail
 * pour lire sa démarche — un `<details>` natif, qui s'ouvre au clavier sans une
 * ligne de script, et dont l'en-tête peut porter un énoncé avec ses
 * mathématiques dessinées. Les filtres sont repliés ; leur résumé dit s'il y en
 * a un de posé.
 *
 * ─── Les filtres vivent dans l'adresse ──────────────────────────────────────
 *
 * `?genre=&notion=&verdict=&erreur=` : un lien « Voir les copies » d'une autre
 * page arrive filtré, et un rechargement garde le filtre. C'est le SERVEUR qui
 * filtre ; l'écran ne trie ni ne recompte rien.
 *
 * ─── Lecture seule, sauf UNE note sur un tracé ──────────────────────────────
 *
 * Aucune route n'annote une copie (MV-DEF-26), À UNE EXCEPTION près depuis
 * l'atelier de figures L7 (décision 3 du fondateur, 17 octobre 2026 : MV-DEF-26
 * assoupli dans ce sens seulement) : sur un essai à TRACER, le tuteur qui suit
 * l'élève touche un objet du tracé et écrit une phrase (`TraceDuTuteur.tsx`).
 * Sans dessin, sans effet sur le verdict. Aucun autre geste d'écriture.
 */
import {
  LIBELLES_VERDICT_HISTORIQUE,
  direSerie,
  type PagesDeTravailTuteurResponse,
  type QuestionDeTravailDto,
  type TravailTuteurDto,
  type TravauxTuteurResponse,
  type VerdictExerciceManuel,
} from '@silex/shared';
import { useEffect, useId, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ApiError, api } from '../../api/client';
import { Badge } from '../../components/Badge';
import { Chargement, ErreurRessource } from '../../components/EtatRessource';
import { DiagrammeWeb, SolideWeb } from '../../components/fiche/DessinWeb';
import { DiagrammeCorrigeWeb } from '../../components/fiche/DiagrammeCorrigeWeb';
import { TableWeb } from '../../components/fiche/TableWeb';
import { BrouillonWeb, FigureV2Web, TracesWeb } from '../../components/FigureV2Web';
import { ListePaginee } from '../../components/Pagination';
import { SectionRabattable } from '../../components/SectionRabattable';
import { TexteMaths } from '../../components/TexteMaths';
import { adresseDeLaPage } from '../../lib/figures-v2-web';
import { nomDeNotion } from '../../lib/manuel-tuteur-format';
import {
  CHOIX_DE_GENRE,
  CHOIX_DE_VERDICT,
  CLES_DE_FILTRE,
  aucunFiltre,
  direCompteTravaux,
  direDureeDeTravail,
  direIndices,
  filtresDeLAdresse,
  type FiltresDeTravaux,
} from '../../lib/manuel-tuteur-lectures';
import type { BadgeTone } from '../../lib/suivi-format';
import { formatDate } from '../../lib/tuteur-format';
import { useAsync } from '../../lib/useAsync';

import { ConstructionRejoueeWeb, NotesDuTuteurSurLeTrace } from './TraceDuTuteur';

/** Jamais de rouge : « à revoir » est en ardoise, comme partout chez le tuteur. */
/** Le paramètre d'adresse de la page des travaux. */
const PARAM_TRAVAUX = 'p-travaux';

const TON_VERDICT: Readonly<Record<VerdictExerciceManuel, BadgeTone>> = {
  juste: 'acquis',
  partiel: 'en-cours',
  erreur: 'a-revoir',
};

export function TravauxEleve({
  eleveId,
  prenom,
  rangChapitre,
}: {
  readonly eleveId: string;
  readonly prenom: string;
  readonly rangChapitre: number;
}) {
  const [params, setParams] = useSearchParams();
  const filtres = filtresDeLAdresse(params);
  const { genre, notion, verdict, erreur } = filtres;
  const resource = useAsync(
    useMemo(
      () => () => api.manuelTravaux(eleveId, { genre, notion, verdict, erreur }),
      [eleveId, genre, notion, verdict, erreur],
    ),
  );

  /* La dernière page lue reste à l'écran pendant qu'un filtre se relit : sans
     cela les filtres eux-mêmes disparaîtraient à chaque choix. */
  const [lu, setLu] = useState<TravauxTuteurResponse | null>(null);
  useEffect(() => {
    if (resource.status === 'ready') setLu(resource.data);
  }, [resource]);
  useEffect(() => setLu(null), [eleveId]);

  const regler = (cle: keyof FiltresDeTravaux, valeur: string): void => {
    const suite = new URLSearchParams(params);
    if (valeur === '') suite.delete(cle);
    else suite.set(cle, valeur);
    suite.delete(PARAM_TRAVAUX); /* un filtre neuf repart de la page 1 (AB#888) */
    setParams(suite, { replace: true });
  };
  const effacer = (): void => {
    const suite = new URLSearchParams(params);
    for (const cle of CLES_DE_FILTRE) suite.delete(cle);
    suite.delete(PARAM_TRAVAUX);
    setParams(suite, { replace: true });
  };

  if (resource.status === 'error') return <ErreurRessource message={resource.message} />;
  if (lu === null) return <Chargement quoi="ses travaux" />;

  return (
    <div className="stack" data-testid="manuel-travaux">
      <Filtres
        reponse={lu}
        filtres={filtres}
        rangChapitre={rangChapitre}
        surChoix={regler}
        surEffacement={effacer}
      />

      {lu.travaux.length === 0 ? (
        <p className="note-line" data-testid="manuel-travaux-vide">
          <strong>
            {aucunFiltre(filtres) ? 'Aucun travail enregistré.' : 'Aucun travail ne correspond.'}
          </strong>
        </p>
      ) : (
        /* AB#888 : par pages de 20, la page dans l'adresse (`p-travaux`). */
        <ListePaginee
          elements={lu.travaux}
          param={PARAM_TRAVAUX}
          singulier="travail"
          pluriel="travaux"
          testId="manuel-travaux-pagines"
        >
          {(page) => (
            <div className="travaux" data-testid="manuel-travaux-liste">
              {page.map((t) => (
                <Travail
                  key={t.copieId}
                  travail={t}
                  prenom={prenom}
                  rangChapitre={rangChapitre}
                  eleveId={eleveId}
                />
              ))}
            </div>
          )}
        </ListePaginee>
      )}
      {lu.visibles > lu.travaux.length ? (
        <p className="note-line">
          Les {lu.travaux.length} plus récents sont affichés. Un filtre resserre la liste.
        </p>
      ) : null}
    </div>
  );
}

/* ─────────────────────────── les filtres, repliés ─────────────────────────── */

function Filtres({
  reponse,
  filtres,
  rangChapitre,
  surChoix,
  surEffacement,
}: {
  readonly reponse: TravauxTuteurResponse;
  readonly filtres: FiltresDeTravaux;
  readonly rangChapitre: number;
  readonly surChoix: (cle: keyof FiltresDeTravaux, valeur: string) => void;
  readonly surEffacement: () => void;
}) {
  const id = useId();
  const sans = aucunFiltre(filtres);
  /* Le résumé nomme le filtre d'erreur : c'est celui qu'un lien « Voir les
     copies » pose, et le tuteur doit savoir pourquoi la liste est courte. */
  const erreurPosee = reponse.erreurs.find((e) => e.code === filtres.erreur);
  const resume = [
    direCompteTravaux(reponse),
    sans
      ? 'aucun filtre'
      : erreurPosee === undefined
        ? 'un filtre est posé'
        : `erreur : ${erreurPosee.titre}`,
  ].join(' · ');

  return (
    <div data-testid="manuel-travaux-filtres">
      <SectionRabattable
        variante="tuile"
        titre="Filtres"
        resume={resume}
        sous={direCompteTravaux(reponse)}
        ouverteParDefaut={false}
        actions={
          sans ? null : (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={surEffacement}
              data-testid="manuel-travaux-effacer"
            >
              Effacer les filtres
            </button>
          )
        }
      >
        <div className="form-grid form-grid--serre">
          <div className="field field--inline">
            <label htmlFor={`${id}-genre`}>Genre</label>
            <select
              id={`${id}-genre`}
              value={filtres.genre}
              onChange={(e) => surChoix('genre', e.target.value)}
              data-testid="manuel-travaux-filtre-genre"
            >
              <option value="">Tous les travaux</option>
              {CHOIX_DE_GENRE.map((c) => (
                <option key={c.valeur} value={c.valeur}>
                  {c.libelle}
                </option>
              ))}
            </select>
          </div>
          <div className="field field--inline">
            <label htmlFor={`${id}-notion`}>Notion</label>
            <select
              id={`${id}-notion`}
              value={filtres.notion}
              onChange={(e) => surChoix('notion', e.target.value)}
              data-testid="manuel-travaux-filtre-notion"
            >
              <option value="">Toutes les notions</option>
              {reponse.notions.map((n) => (
                <option key={n.code} value={n.code}>
                  {nomDeNotion(n, rangChapitre)}
                </option>
              ))}
            </select>
          </div>
          <div className="field field--inline">
            <label htmlFor={`${id}-verdict`}>Verdict</label>
            <select
              id={`${id}-verdict`}
              value={filtres.verdict}
              onChange={(e) => surChoix('verdict', e.target.value)}
              data-testid="manuel-travaux-filtre-verdict"
            >
              <option value="">Tous les verdicts</option>
              {CHOIX_DE_VERDICT.map((c) => (
                <option key={c.valeur} value={c.valeur}>
                  {c.libelle}
                </option>
              ))}
            </select>
          </div>
          <div className="field field--inline">
            <label htmlFor={`${id}-erreur`}>Erreur</label>
            <select
              id={`${id}-erreur`}
              value={filtres.erreur}
              onChange={(e) => surChoix('erreur', e.target.value)}
              data-testid="manuel-travaux-filtre-erreur"
            >
              <option value="">Toutes les erreurs</option>
              {reponse.erreurs.map((e) => (
                <option key={e.code} value={e.code}>
                  {e.titre} — {e.fois} fois
                </option>
              ))}
            </select>
          </div>
        </div>
      </SectionRabattable>
    </div>
  );
}

/* ─────────────────────────── un travail, dépliable ─────────────────────────── */

/** « 2e essai · 8 octobre · 1.2 · Multiplier des puissances · Facile ». */
function direEnTete(t: TravailTuteurDto, rangChapitre: number): string {
  return [
    t.rangEssai === null ? null : t.rangEssai === 1 ? '1er essai' : `${t.rangEssai}e essai`,
    formatDate(t.corrigeLe),
    ...t.notions.map((n) => nomDeNotion(n, rangChapitre)),
    t.nomNiveau ?? t.libelleGenre,
  ]
    .filter((m): m is string => m !== null)
    .join(' · ');
}

/** Un travail, une fois lu : sans appel, il s'éprouve par son rendu. */
export function Travail({
  travail: t,
  prenom,
  rangChapitre,
  eleveId,
}: {
  readonly travail: TravailTuteurDto;
  readonly prenom: string;
  readonly rangChapitre: number;
  /** Pour lire, à la demande, les pages au stylet d'un essai. Absent : elles ne sont pas offertes. */
  readonly eleveId?: string;
}) {
  const faits = [
    t.libelleOrigine,
    direDureeDeTravail(t.dureeSecondes),
    direIndices(t.aidesDemandees),
    t.genre === 'devoir' ? 'Transcrit depuis une photo' : null,
  ].filter((m): m is string => m !== null);

  return (
    <details className="travail" data-testid="manuel-travail">
      <summary className="travail__tete">
        <span className="travail__titre">
          <span className="li-title">
            <TexteMaths texte={t.titre} />
          </span>
          <span className="li-sub">{direEnTete(t, rangChapitre)}</span>
          {/* `UX-7-31` : une erreur se lit comme une phrase, pas comme une
              étiquette en capitales de soixante caractères. */}
          {t.erreurs.map((e) => (
            <span
              key={e.code}
              className="li-sub travail__erreur"
              data-testid="manuel-travail-erreur"
            >
              Erreur : {e.titre}
            </span>
          ))}
        </span>
        <Badge tone={TON_VERDICT[t.verdict]} label={t.libelleVerdict} />
      </summary>

      <div className="travail__corps" data-testid="manuel-travail-corps">
        <p className="cell-sub">{faits.join(' · ')}</p>
        {eleveId === undefined ? null : (
          <RetourDuTravail eleveId={eleveId} copieId={t.copieId} prenom={prenom} />
        )}
        {t.demarche === null || t.demarche.length === 0 ? (
          <p className="note-line">Cette copie ne porte pas de démarche à relire.</p>
        ) : (
          t.demarche.map((q) => (
            <Question
              key={q.id}
              question={q}
              prenom={prenom}
              rangChapitre={rangChapitre}
              devoir={t.genre === 'devoir'}
              pages={eleveId === undefined ? null : { eleveId, copieId: t.copieId }}
            />
          ))
        )}
      </div>
    </details>
  );
}

/**
 * LE RETOUR RENDU À L'ÉLÈVE (chantier « Rétroaction », P1-E) : la VUE TUTEUR
 * du gabarit unique — le verdict, le travail annoté, chaque constat, le plan,
 * la provenance de chaque champ et le coût. Le serveur rend un document HTML
 * autonome ; la console le pose dans un cadre ISOLÉ (`sandbox` vide : aucun
 * script, aucune origine), lu à la demande comme les pages au stylet.
 */
function RetourDuTravail({
  eleveId,
  copieId,
  prenom,
}: {
  readonly eleveId: string;
  readonly copieId: string;
  readonly prenom: string;
}) {
  const [etat, setEtat] = useState<
    | { readonly phase: 'repos' | 'lecture' | 'erreur' }
    | { readonly phase: 'absent'; readonly message: string }
    | { readonly phase: 'lu'; readonly html: string }
  >({ phase: 'repos' });
  const lire = (): void => {
    if (etat.phase === 'lecture' || etat.phase === 'lu' || etat.phase === 'absent') return;
    setEtat({ phase: 'lecture' });
    api
      .manuelRetroactionDuTravail(eleveId, copieId)
      .then((html) => setEtat({ phase: 'lu', html }))
      .catch((e: unknown) =>
        setEtat(
          e instanceof ApiError && e.code === 'RETROACTION_ABSENTE'
            ? { phase: 'absent', message: e.message }
            : { phase: 'erreur' },
        ),
      );
  };
  return (
    <details
      className="sequence-raison retour-travail"
      data-testid="manuel-travail-retour"
      onToggle={(e) => {
        if (e.currentTarget.open) lire();
      }}
    >
      <summary data-testid="manuel-travail-retour-voir">Le retour rendu à {prenom}</summary>
      {etat.phase === 'lu' ? (
        <iframe
          className="retour-travail__cadre"
          data-testid="manuel-travail-retour-cadre"
          title={`Le retour rendu à ${prenom}, vue tuteur`}
          sandbox=""
          srcDoc={etat.html}
        />
      ) : (
        <p className="cell-sub" role={etat.phase === 'erreur' ? 'alert' : undefined}>
          {etat.phase === 'absent'
            ? etat.message
            : etat.phase === 'erreur'
              ? 'Le retour ne se lit pas pour l’instant. Referme, puis rouvre pour réessayer.'
              : 'Lecture du retour…'}
        </p>
      )}
    </details>
  );
}

/**
 * LES PAGES AU STYLET d'un essai (tranche T2) : ce que l'élève a réellement
 * écrit, avant toute lecture. Lues À LA DEMANDE — quatre images par essai ne
 * voyagent pas avec la liste —, à l'ouverture d'un `<details>` natif, comme un
 * travail : aucun bouton, donc aucun geste qu'on prendrait pour une écriture
 * (MV-DEF-26 : l'onglet est en lecture seule).
 */
function PagesAuStylet({
  eleveId,
  copieId,
  attendues,
  prenom,
}: {
  readonly eleveId: string;
  readonly copieId: string;
  readonly attendues: number;
  readonly prenom: string;
}) {
  const [etat, setEtat] = useState<
    | { readonly phase: 'repos' | 'lecture' | 'erreur' }
    | { readonly phase: 'lues'; readonly pages: PagesDeTravailTuteurResponse['pages'] }
  >({ phase: 'repos' });
  const lire = (): void => {
    if (etat.phase === 'lecture' || etat.phase === 'lues') return;
    setEtat({ phase: 'lecture' });
    api
      .manuelPagesDeTravail(eleveId, copieId)
      .then((r) => setEtat({ phase: 'lues', pages: r.pages }))
      .catch(() => setEtat({ phase: 'erreur' }));
  };
  return (
    <details
      className="sequence-raison pages-stylet"
      data-testid="manuel-travail-pages"
      onToggle={(e) => {
        if (e.currentTarget.open) lire();
      }}
    >
      <summary data-testid="manuel-travail-pages-voir">
        {attendues === 1
          ? `La page que ${prenom} a écrite au stylet`
          : `Les ${attendues} pages que ${prenom} a écrites au stylet`}
      </summary>
      {etat.phase === 'lues' ? (
        etat.pages.map((page, i) => (
          <img
            key={i}
            className="pages-stylet__page"
            src={adresseDeLaPage(page)}
            alt={`Page ${i + 1} sur ${etat.pages.length}, écrite au stylet par ${prenom}`}
          />
        ))
      ) : (
        <p className="cell-sub" role={etat.phase === 'erreur' ? 'alert' : undefined}>
          {etat.phase === 'erreur'
            ? 'Les pages ne se lisent pas pour l’instant. Referme, puis rouvre pour réessayer.'
            : 'Lecture des pages…'}
        </p>
      )}
    </details>
  );
}

function Question({
  question: q,
  prenom,
  rangChapitre,
  devoir,
  pages,
}: {
  readonly question: QuestionDeTravailDto;
  readonly prenom: string;
  readonly rangChapitre: number;
  readonly devoir: boolean;
  /** De quoi lire les pages au stylet de l'essai ; `null` : non offertes ici. */
  readonly pages: { readonly eleveId: string; readonly copieId: string } | null;
}) {
  return (
    <section className="travail__question" data-testid="manuel-travail-question">
      <div className="row-between wrap">
        <div>
          <strong>
            {q.etiquette === null ? null : `${q.etiquette} `}
            <TexteMaths texte={q.enonce} />
          </strong>
          {q.notion === null ? null : (
            <span className="cell-sub">{nomDeNotion(q.notion, rangChapitre)}</span>
          )}
        </div>
        {q.verdict === null ? (
          <span className="cell-sub">Non corrigée</span>
        ) : (
          <Badge
            tone={TON_VERDICT[q.verdict]}
            label={q.libelleVerdict ?? LIBELLES_VERDICT_HISTORIQUE[q.verdict]}
          />
        )}
      </div>

      {/* CE QUE L'ÉLÈVE AVAIT SOUS LES YEUX (tranche T2) : la figure de l'énoncé,
          gardée avec l'essai. Pour un tracé, elle est le calque sur lequel il a
          tracé : elle se montre avec lui, pas deux fois. */}
      {q.figure !== undefined && q.trace === undefined ? (
        <FigureV2Web
          figure={q.figure.figure}
          decrit={q.figure.decrit}
          testid="manuel-travail-figure"
        />
      ) : null}

      {/* Une table de valeurs (`CAP-09`) : celle de l'énoncé, puis celle que
          l'élève a remplie, corrigée case par case. */}
      {q.tableDEnonce !== undefined && q.table === undefined ? (
        <TableWeb table={q.tableDEnonce} testid="manuel-travail-table-enonce" />
      ) : null}
      {q.table !== undefined ? (
        <>
          <h4>La table que {prenom} a remplie</h4>
          <TableWeb table={q.table} testid="manuel-travail-table" />
        </>
      ) : null}

      {/* Un solide (`CAP-11`) : celui de l'énoncé, puis la figure que l'élève a choisie. */}
      {q.solideDEnonce !== undefined ? (
        <SolideWeb solide={q.solideDEnonce} testid="manuel-travail-solide-enonce" />
      ) : null}
      {q.solide !== undefined ? (
        <>
          <h4>La figure que {prenom} a choisie</h4>
          <SolideWeb solide={q.solide} fautifPermis testid="manuel-travail-solide-choisi" />
        </>
      ) : null}

      {/* Un diagramme statistique (`CAP-08`) : celui de l'énoncé, puis celui que
          l'élève a construit, corrigé élément par élément. */}
      {q.diagrammeDEnonce !== undefined && q.diagramme === undefined ? (
        <DiagrammeWeb
          diagramme={q.diagrammeDEnonce.diagramme}
          decrit={q.diagrammeDEnonce.decrit}
          testid="manuel-travail-diagramme-enonce"
        />
      ) : null}
      {q.diagramme !== undefined ? (
        <>
          <h4>Le diagramme que {prenom} a construit</h4>
          <DiagrammeCorrigeWeb corrige={q.diagramme} testid="manuel-travail-diagramme" />
        </>
      ) : null}

      {/* La série simulée (atelier de figures, L5, F11) : jamais corrigée, rejouable par sa graine. */}
      {q.simulation !== undefined ? (
        <>
          <h4>La série que {prenom} a simulée</h4>
          <p className="note-line" data-testid="manuel-travail-simulation">
            {direSerie(q.simulation)} Graine {q.simulation.graine} : la même graine rejoue la même
            série.
          </p>
        </>
      ) : null}

      {/* LA FIGURE QUI RAISONNE (atelier de figures, L4, F5) : la partie
          « construction » d'une SP, corrigée par le code ; puis le brouillon de
          figure joint à la démarche — en lecture seule, jamais noté. */}
      {q.partieConstruite !== undefined ? (
        <>
          <h4>La partie construite</h4>
          <p className="note-line" data-testid="manuel-travail-partie">
            <TexteMaths texte={q.partieConstruite.consigne} />{' '}
            {q.partieConstruite.verdict === null ? (
              <span className="cell-sub">Pas tracée.</span>
            ) : (
              <Badge
                tone={TON_VERDICT[q.partieConstruite.verdict]}
                label={LIBELLES_VERDICT_HISTORIQUE[q.partieConstruite.verdict]}
              />
            )}
          </p>
          {q.partieConstruite.trace !== undefined ? (
            <TracesWeb
              trace={q.partieConstruite.trace}
              figureDEnonce={q.figure?.figure}
              prenom={prenom}
            />
          ) : null}
        </>
      ) : null}
      {q.brouillonFigure !== undefined ? (
        <>
          <h4>Le brouillon de figure de {prenom}</h4>
          <p className="note-line">
            Joint à sa démarche pour réfléchir. Il n’est jamais noté ni lu par un modèle.
          </p>
          <BrouillonWeb
            document={q.brouillonFigure}
            figureDEnonce={q.figure?.figure}
            prenom={prenom}
          />
        </>
      ) : null}

      {q.trace !== undefined ? (
        <>
          <h4>Ce que {prenom} a tracé</h4>
          <TracesWeb trace={q.trace} figureDEnonce={q.figure?.figure} prenom={prenom} />
          {/* Atelier de figures, L7 : la construction rejouée (lecture seule) et
              la note du tuteur ancrée à un objet (option B). */}
          <ConstructionRejoueeWeb
            trace={q.trace}
            figureDEnonce={q.figure?.figure}
            prenom={prenom}
          />
          {pages !== null && !devoir ? (
            <NotesDuTuteurSurLeTrace
              eleveId={pages.eleveId}
              copieId={pages.copieId}
              trace={q.trace}
              figureDEnonce={q.figure?.figure}
              prenom={prenom}
            />
          ) : null}
        </>
      ) : null}

      {q.pagesManuscrites !== undefined && pages !== null ? (
        <PagesAuStylet {...pages} attendues={q.pagesManuscrites} prenom={prenom} />
      ) : null}

      {q.trace !== undefined && q.lignes.length === 0 ? null : <h4>Ce que {prenom} a écrit</h4>}
      {q.lignes.length === 0 ? (
        q.trace !== undefined ? null : (
          <p className="note-line">Aucune démarche écrite.</p>
        )
      ) : (
        <ol className="demarche" data-testid="manuel-travail-demarche">
          {q.lignes.map((l) => (
            <li key={l.n} className="demarche__ligne" data-testid="manuel-travail-ligne">
              <span className="demarche__ecrit">
                <TexteMaths texte={l.ecrit} />
              </span>
              <span className="demarche__lecture">
                {l.verdict === null ? (
                  <span className="cell-sub">Sans remarque</span>
                ) : (
                  <Badge
                    tone={TON_VERDICT[l.verdict]}
                    label={l.libelleVerdict ?? LIBELLES_VERDICT_HISTORIQUE[l.verdict]}
                  />
                )}
                {l.erreur === null ? null : (
                  <span className="li-sub li-sub--attention">Erreur : {l.erreur.titre}</span>
                )}
                {l.commentaire === null ? null : (
                  <span className="li-sub">
                    <TexteMaths texte={l.commentaire} />
                  </span>
                )}
                {l.surligner === null ? null : (
                  <span className="li-sub">
                    En cause : <TexteMaths texte={l.surligner} />
                  </span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
      {q.reponse === null ? null : (
        <p className="travail__reponse">
          <span className="lbl">Réponse finale</span> <TexteMaths texte={q.reponse} />
        </p>
      )}

      {q.retroaction === null ? null : (
        <>
          <h4>Ce que {prenom} a lu</h4>
          <p className="travail__retroaction">
            <TexteMaths texte={q.retroaction} />
          </p>
        </>
      )}

      {q.reference === null ? (
        devoir ? (
          <p className="cell-sub">
            Devoir de l’école : pas de démarche de référence dans la banque.
          </p>
        ) : null
      ) : (
        <details className="sequence-raison">
          <summary>Démarche de référence</summary>
          <ol className="plan">
            {q.reference.map((r, i) => (
              <li key={i}>
                <TexteMaths texte={r.ecrit} />{' '}
                <span className="cell-sub">
                  <TexteMaths texte={r.etape} />
                </span>
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}
